import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeValueFacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ArrowLeft from "@insite/mobius/Icons/ArrowLeft";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export interface ProductListFilterStyles {
    title?: InjectableCss;
    wrapper?: InjectableCss;
    container?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        links: state.components.breadcrumbs.links,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const productListAttributeFiltersStyles: ProductListFilterStyles = {
    container: {
        css: css`
            margin-bottom: 40px;
        `,
    },
    title: {
        css: css`
            font-size: 17px;
            font-weight: 700;
        `,
    },
    wrapper: {
        css: css`
            margin-top: 10px;
            margin-bottom: 20px;
        `,
    },
};

const styles = productListAttributeFiltersStyles;
interface PillButtonProps extends ButtonPresentationProps {
    selected?: boolean;
}

const PillButton = styled(Button)<PillButtonProps>`
    margin: 5px;
    font-size: 14px;
    background-color: ${props => (props.selected ? "#B7312C" : "#FFFFFF")};
    color: ${props => (props.selected ? "white" : "black")};
    border: ${props => (props.selected ? "1px solid #B7312C" : "1px solid black")};
    &:hover {
        background-color: ${props => (props.selected ? "#B7312C" : "#FFFFFF")};
        border: ${props => (props.selected ? "1px solid #B7312C" : "1px solid black")};
    }
`;

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 10px;
    align-items: center;
`;

const PaginationIcon = styled.div<{ disabled?: boolean }>`
    margin: 0 5px;
    cursor: pointer;
    opacity: ${props => (props.disabled ? 0.5 : 1)};
    pointer-events: ${props => (props.disabled ? "none" : "auto")};
    display: flex;
    align-items: center;
`;

const PaginationCount = styled(Typography)`
    margin: 0 10px;
    min-width: 60px; /* Ensures that the text stays centered even if it changes */
    text-align: center;
`;

const ProductListAttributeFilters: FC<Props> = ({
    attributeTypeFacets,
    addProductFilters,
    removeProductFilters,
    links,
}) => {
    const lastCategory = links?.[links.length - 1] || null;
    const itemsPerPage = 12;
    const [currentPage, setCurrentPage] = useState(1);

    if (!attributeTypeFacets?.length) {
        return null;
    }

    const onChangeFacet = (facet: AttributeValueFacetModel & { selected: boolean }) => {
        if (facet.selected) {
            removeProductFilters({ attributeValueIds: [facet.attributeValueId] });
        } else {
            addProductFilters({ attributeValueIds: [facet.attributeValueId] });
        }
    };

    const handleNextPage = (totalPages: number) => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <>
            {attributeTypeFacets
                ?.filter(ab => ab.name === "Item Type")
                ?.map(a => {
                    const totalItems = a?.attributeValueFacets?.length || 0;
                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const paginatedFacets = a.attributeValueFacets?.slice(startIndex, startIndex + itemsPerPage);

                    return (
                        a.attributeValueFacets && (
                            <StyledWrapper key={a.attributeTypeId} {...styles.container}>
                                <Typography {...styles.title}>{`Shop ${lastCategory?.children}`}</Typography>
                                <StyledWrapper {...styles.wrapper}>
                                    {paginatedFacets?.map(facet => (
                                        <PillButton
                                            key={facet.attributeValueId}
                                            selected={facet.selected}
                                            onClick={() => onChangeFacet(facet)}
                                        >
                                            {facet.valueDisplay} {facet.count !== -1 && `(${facet.count})`}
                                        </PillButton>
                                    ))}
                                </StyledWrapper>
                                {totalItems > itemsPerPage && (
                                    <PaginationWrapper>
                                        <PaginationIcon onClick={handlePreviousPage} disabled={currentPage === 1}>
                                            <ArrowLeft />
                                        </PaginationIcon>
                                        <PaginationCount>{`${currentPage} of ${totalPages}`}</PaginationCount>
                                        <PaginationIcon
                                            onClick={() => handleNextPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <ArrowRight />
                                        </PaginationIcon>
                                    </PaginationWrapper>
                                )}
                            </StyledWrapper>
                        )
                    );
                })}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListAttributeFilters),
    definition: {
        group: "Product List",
        displayName: "Attribute Filters Custom",
    },
};

export default widgetModule;
